import React from 'react';
import { Checkbox } from 'antd';
import { PaperClipOutlined } from '@ant-design/icons';
import CurrencyFormat from 'react-currency-format';
import dayjs from 'dayjs';
import PACommentsDisplay from '../PACommentsDisplay';
import StatusStripe, { normalizeAggregatedItemsStatus } from '../StatusStripe';
import { uri_img } from '../../../assets/utils/http-request';
import { paidLabel } from './paymentAdvice';
import {
  getDisplayCost,
  getPaApprovedTotal,
  getSubmittedTotal,
} from '../claimFinancialHelpers';

function Money({ value }) {
  return (
    <CurrencyFormat
      value={Number(value) || 0}
      displayType="text"
      thousandSeparator
      prefix="₦"
      decimalScale={2}
      fixedDecimalScale
    />
  );
}

function shortDate(value) {
  if (!value) return null;
  const d = dayjs(value);
  if (!d.isValid()) return null;
  return d.format('MMM D');
}

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
}

function getSourceMeta(claim) {
  const mode = String(
    claim?.source_mode ||
      (claim?.source !== undefined ? (Number(claim.source) === 1 ? 'manual' : 'direct') : 'direct')
  ).toLowerCase();
  const manualCount = Number(claim?.manual_items_count || 0);
  const directCount = Number(claim?.direct_items_count || 0);
  return {
    cls: mode === 'mixed' ? 'drill-tag-mixed' : mode === 'manual' ? 'drill-tag-manual' : 'drill-tag-direct',
    label:
      mode === 'mixed'
        ? `Mixed${manualCount || directCount ? ` M:${manualCount} D:${directCount}` : ''}`
        : mode === 'manual'
          ? 'Manual'
          : 'Direct',
  };
}

function getReviewMeta(claim) {
  const reviewRequired = Number(claim?.review_required ?? 0);
  const rawComments = typeof claim?.claims_comment === 'string' ? claim.claims_comment : '';
  const commentSegments = rawComments.split('|break|').map((s) => s.trim()).filter(Boolean);
  const medicalPrefix = 'medical comment';
  const medicalSegments = commentSegments.filter((s) => s.toLowerCase().startsWith(medicalPrefix));
  const hasHMReview = reviewRequired === 1;
  const isHMResolutionPending = reviewRequired === 2;
  const latestSnippet =
    (medicalSegments.length ? medicalSegments[medicalSegments.length - 1] : '') ||
    (commentSegments.length ? commentSegments[commentSegments.length - 1] : '');
  const latestSnippetDisplay = latestSnippet ? latestSnippet.replace(/>>>/g, '→') : '';
  return { hasHMReview, isHMResolutionPending, latestSnippetDisplay };
}

function hasPAComments(claim) {
  return !!(
    claim?.pa_comment ||
    claim?.batch_comment ||
    (Array.isArray(claim?.pa_comments) && claim.pa_comments.length > 0)
  );
}

function stageShortLabel(claim, segment) {
  if (segment === 3 && claim.date_paid) {
    return `${paidLabel(claim)} ${shortDate(claim.date_paid)}`;
  }
  if (segment === 1 && claim.date_forwarded_hm) {
    return `HM ${shortDate(claim.date_forwarded_hm)}`;
  }
  if (segment === 2 && claim.date_forwarded_cfo) {
    return `CFO ${shortDate(claim.date_forwarded_cfo)}`;
  }
  if (claim.claims_date) {
    return `Filed ${shortDate(claim.claims_date)}`;
  }
  return null;
}

/**
 * Table-row rendering of a claim for the payment claims drill-down.
 * Same data/actions as PaymentClaimCard, laid out as table cells instead of a card.
 */
export default function PaymentClaimTableRow({
  claim,
  segment,
  selectionMode,
  isSelected,
  onToggleSelect,
  onClick,
}) {
  const status = claim.aggregatedItemsStatus || {};
  const lineCounts = normalizeAggregatedItemsStatus(claim.aggregatedItemsStatus);
  const sourceMeta = getSourceMeta(claim);
  const { hasHMReview, isHMResolutionPending, latestSnippetDisplay } = getReviewMeta(claim);
  const patientName = claim.patient_name || 'Unknown patient';
  const meta = claim.insurance_no || claim.checkin_id || claim.claims_id || 'No ID';
  const photoSrc = claim.pic_name ? `${uri_img}${claim.pic_name}` : null;
  const encounterDate = shortDate(claim.encounter_date);
  const showComments = hasPAComments(claim);
  const showNote = (hasHMReview || isHMResolutionPending) && latestSnippetDisplay;
  const hasDocs = Boolean(
    claim.has_pa_header_attachments ||
      (Array.isArray(claim.pa_header_attachments) && claim.pa_header_attachments.length > 0)
  );

  const handleClick = () => {
    if (selectionMode) {
      onToggleSelect?.(!isSelected);
      return;
    }
    onClick?.();
  };

  const rowClassName = [
    hasHMReview ? 'is-returned' : '',
    selectionMode && isSelected ? 'is-selected' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      <tr
        className={rowClassName}
        onClick={handleClick}
        onKeyDown={(e) => e.key === 'Enter' && handleClick()}
        tabIndex={0}
      >
        <td>
          <div className="patient-cell">
            {photoSrc ? (
              <img className="patient-photo" src={photoSrc} alt="" />
            ) : (
              <span className="patient-photo">{initials(patientName)}</span>
            )}
            <div>
              <div className="patient-name">{patientName}</div>
              <div className="patient-meta">{meta}</div>
            </div>
          </div>
        </td>
        <td>
          {encounterDate || '—'}
          {stageShortLabel(claim, segment) ? (
            <div className="patient-meta">{stageShortLabel(claim, segment)}</div>
          ) : null}
        </td>
        <td className="num"><Money value={getSubmittedTotal(claim)} /></td>
        <td className="num"><Money value={getPaApprovedTotal(claim)} /></td>
        <td className="num is-accent"><Money value={getDisplayCost(claim)} /></td>
        <td>
          {lineCounts ? (
            <StatusStripe counts={lineCounts} compact className="payment-claim-row__stripe" />
          ) : status.text ? (
            <span className={`drill-tag ${status.code === 1 ? 'drill-tag-approved' : 'drill-tag-pending'}`}>
              {status.text}
            </span>
          ) : null}
          <span className={`drill-tag ${sourceMeta.cls}`}>{sourceMeta.label}</span>
          {hasHMReview ? <span className="drill-tag drill-tag-returned">Returned</span> : null}
          {isHMResolutionPending ? <span className="drill-tag drill-tag-hmreview">HM review</span> : null}
          {hasDocs ? <span className="drill-tag drill-tag-docs"><PaperClipOutlined /> Docs</span> : null}
          {claim.queries > 0 ? (
            <span className="drill-tag drill-tag-pending">{claim.queries} quer{claim.queries === 1 ? 'y' : 'ies'}</span>
          ) : null}
        </td>
        <td className="claims-table-actions" onClick={(e) => e.stopPropagation()}>
          {showComments ? (
            <PACommentsDisplay claim={claim} variant="trigger" className="payment-claim-card__comments-btn" />
          ) : null}
          {selectionMode ? (
            <div className="claims-row-checkbox">
              <Checkbox checked={isSelected} onChange={(e) => onToggleSelect?.(e.target.checked)} />
            </div>
          ) : (
            <span className="drill-icon-chip">›</span>
          )}
        </td>
      </tr>
      {showNote ? (
        <tr className="claims-drill-note-row">
          <td colSpan={7}>
            <div className={`claims-drill-note${hasHMReview ? '' : ' is-warn'}`} title={latestSnippetDisplay}>
              {latestSnippetDisplay}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}
